
    import type { Player } from "@/cuties/model/player/Player";

    import { Component, Prop, Watch } from "vue-property-decorator";
    import { BigNumber } from "bignumber.js";
    import LoginManager from "@/cuties/LoginManager";
    import { Blockchain } from "@/cuties/model/pet/BlockchainId";
    import type { StakeContract } from "@/cuties/blockchain/ethereum/EthereumStakingService";
    import { EthereumStakingService } from "@/cuties/blockchain/ethereum/EthereumStakingService";
    import detectEthereumProvider from "@metamask/detect-provider";
    import type EthereumProvider from "@walletconnect/ethereum-provider/dist/types/EthereumProvider";
    import Web3MetamaskProvider from "@/cuties/blockchain/matic/Web3MetamaskProvider";
    import utils from "@/cuties/utils";
    import VueUtils, { getDecimalsError } from "@/cuties/VueUtils";
    import PlayerEnvironment from "@/cuties/player/PlayerEnvironment";
    import { EthereumUniswapService } from "@/cuties/blockchain/ethereum/EthereumUniswapService";
    import { DefaultCatch } from "catch-decorator-ts";
    import { defaultErrorHandler } from "@/common/utils/defaultErrorHandler";
    import type { FormRules } from "@/common/types/FormRules";
    import { mixins } from "vue-class-component";
    import WalletPopup from "@/components/blockchain-wallets/WalletPopup.vue";
    import type { AmountForm } from "@/components/blockchain-wallets/AmountPopup.vue";
    import type { ElForm } from "element-ui/types/form";
    import AppInput from "@/components/app-components/AppInput.vue";
    import { isAmountValid, isNumberValid } from "@/common/formValidator";
    import { ConfigInstance } from "@/cuties/engine/Config";
    import CutiesApiFaucet from "@/app/cuties/blockchain/CutiesApiFaucet";
    import { Getter } from "vuex-class";
    import type { RuleItem } from "async-validator/dist-types/interface";

    @Component({
        components: { AppInput },
    })
    export default class StakingTab extends mixins(WalletPopup) {
        @Prop({ required: true }) tab!: string;
        @Prop({ default: false }) showUsd!: boolean;
        @Prop({ required: true }) contractName!: StakeContract;
        @Getter("getPlayer") user!: Player;

        stakeCount = "";
        stakeCountValue = "";

        allowed = false;
        allowedCount = 0;
        supply = "0";
        totalSupply = new BigNumber(0);
        staked = "0";
        stakedFull = "0";
        stakedNumber = new BigNumber(0);
        stakedUsd = "0";
        stakedIsZero = true;
        balanceUsd = "0";
        balanceBlockchain = "0";
        balanceFull = "0";
        balanceIsZero = true;
        balanceNumber = new BigNumber(0);
        rewardRate = new BigNumber(0);
        rewardIsZero = true;
        rewards = "0";
        rewardsUsd = "0";
        rewardsFull = "0";
        lpPrice = new BigNumber(0);
        rewardLoading = false;
        showConfirm = false;
        popupConfirmText = "";
        showMetamskWarning = false;
        showConfirmExit = false;
        stakeConfirmCount = 0;
        unstakeConfirmCount = 0;
        allowConfirmCount = 0;
        rewardConfirmCount = 0;

        loadingBalance = false;
        loadingReward = false;
        loadingStaked = false;
        loadingAllowed = false;
        loadingStakeBtn = false;
        loadingUnstakeBtn = false;
        loadingAllowBtn = false;
        loadingRewardBtn = false;
        loadingApy = false;
        loadingSupply = false;

        duration = 60 * 60 * 24 * 365;

        confirmationCount = 8;
        tooltipOpenDelay: number = VueUtils.tooltipOpenDelay;
        tooltipCloseDelay: number = VueUtils.tooltipCloseDelay;

        anonymous = false; // user access the page without wallet provider (session)
        activeMetamaskAccount: string | null = null;
        stakingService: EthereumStakingService | null = null;
        uniswapService: EthereumUniswapService | null = null;
        blockchain = Blockchain.Ethereum;
        error = "";

        $refs: {
            sendForm: ElForm;
        };

        form: AmountForm = {
            amount: "0.1",
        };

        decimalsValidator(...args: Parameters<RuleItem["validator"]>): void {
            const [rule, value, callback] = args;
            const errorIfAny = getDecimalsError(this.form.amount, this.blockchain, this.tab);
            callback(errorIfAny);
        };

        get rules(): FormRules {
            return {
                amount: [
                    { required: true, message: this.translatedErrors.FIELD_REQUIRED, trigger: "blur" },
                    { validator: this.isNumberValidator, trigger: ["blur", "change"] },
                    { validator: this.zeroValidator, trigger: ["blur", "change"] },
                    { validator: this.decimalsValidator, trigger: ["blur", "change"] },
                ],
            };
        }

        validNumber = false;

        get stakedPercent(): string {
            if (this.totalSupply.isZero()) {
                return "0";
            } else {
                return this.stakedNumber.dividedBy(this.totalSupply).multipliedBy(100).toFormat(2, 1).toString();
            }
        }

        get apyPercent(): string {
            if (this.tab == "BCUG") {
                return this.rewardRate
                    .multipliedBy(new BigNumber(this.duration))
                    .dividedBy(this.totalSupply)
                    .multipliedBy(new BigNumber(100))
                    .toFormat(2, 1)
                    .toString();
            } else {
                return this.rewardRate
                    .multipliedBy(new BigNumber(this.duration))
                    .multipliedBy(new BigNumber(ConfigInstance.dynamic.usdPrices["BCUG"]))
                    .dividedBy(this.totalSupply.multipliedBy(this.lpPrice))
                    .multipliedBy(new BigNumber(100))
                    .toFormat(2, 1)
                    .toString();
            }
        }

        created(): void {
            this.checkUnauthorizedMetamask()
                .then(() => this.updateData())
                .catch(() => {
                    this.showMetamskWarning = true;
                });
        }

        getEnv(): PlayerEnvironment {
            return PlayerEnvironment;
        }

        isMobile(): boolean {
            return window.innerWidth < 800;
        }

        stakeMax(): void {
            this.form.amount = this.balanceNumber.toFixed(2, 1);
        }

        unstakeMax(): void {
            this.form.amount = this.stakedNumber.toFixed().toString();
        }

        closeMetamaskPopup(): void {
            this.showMetamskWarning = false;
        }

        closePopup(): void {
            this.showConfirm = false;
            this.showConfirmExit = false;
        }

        get isStakeCountCorrect(): boolean {
            if (!this.form.amount.length) {
                return false;
            }

            if (!isNumberValid(this.form.amount.trim())) {
                return false;
            }

            if (new BigNumber(this.form.amount.trim()).isZero()) {
                return false;
            }

            if (!isAmountValid(this.form.amount, this.blockchain)) {
                return false;
            }

            return true;
        }

        @Watch("form.amount")
        cleanError(): void {
            this.error = "";
        }

        @DefaultCatch(defaultErrorHandler)
        async showStakeConfirm(): Promise<void> {
            this.cleanError();
            if (!this.isStakeCountCorrect) {
                return;
            }

            if (new BigNumber(this.form.amount).isGreaterThan(this.balanceNumber.toFixed(2, 1))) {
                this.error = this.$t("stake-error-max-stake").toString() + this.balanceBlockchain + " " + this.tab;
                return;
            }

            if (await this.metamaskWrongAddress()) {
                this.error = this.$t("staking-wrong-address", [this.user.get_ethereum_wallet_id(), this.activeMetamaskAccount]) + "";
                return;
            }

            this.stakeCountValue = this.form.amount;
            this.showConfirm = true;
            this.popupConfirmText = "staking-confirm-text-stake";
        }

        async showUnstakeConfirm(exit?: boolean): Promise<void> {
            if (!exit) {
                if (!this.isStakeCountCorrect) {
                    return;
                }

                if (new BigNumber(this.form.amount).isGreaterThan(this.stakedNumber)) {
                    this.error = this.$t("stake-error-max-unstake").toString() + this.stakedNumber + " " + this.tab;
                    return;
                }
            }

            if (await this.metamaskWrongAddress()) {
                this.error = this.$t("staking-wrong-address", [this.user.get_ethereum_wallet_id(), this.activeMetamaskAccount]) + "";
                return;
            }

            if (exit) {
                this.stakeCountValue = this.stakedNumber.toFixed().toString();
                this.showConfirmExit = true;
            } else {
                this.stakeCountValue = this.form.amount;
                this.showConfirm = true;
                this.popupConfirmText = "staking-confirm-text-unstake";
            }
        }

        async updateData(): Promise<void> {
            await Promise.all([
                this.updateBalance(),
                this.updateStakedBalance(),
                this.updateRewards(),
                this.getAllowedStaking(),
                this.updateTotalSupply(),
                this.getRewardRate(),
            ]).catch((error) => {
                utils.show_notification("Failed to update data - " + error);
                console.error(error);
            });
        }

        getUserEthereumAddress(): string {
            return this.user.get_ethereum_wallet_id() ?? this.activeMetamaskAccount;
        }

        async metamaskWrongAddress(): Promise<boolean> {
            // if metamask active address in other than game address
            // some times provider is metamask but activeMetamaskAccount is not filled (when gameProvider used)

            if (!this.anonymous) {
                // do not check user address when it has session wallet provider
                return;
            }

            // Check is only for registred users
            if (!this.user.get_ethereum_wallet_id()) {
                return false;
            }

            if (!this.activeMetamaskAccount) {
                await this.initProvider(false);
            }

            return (
                !!this.user.get_ethereum_wallet_id() &&
                this.activeMetamaskAccount.toLocaleLowerCase() !== this.user.get_ethereum_wallet_id().toLocaleLowerCase()
            );
        }

        get formattedAddress(): string {
            const address = this.getUserEthereumAddress();
            if (address && address.length) {
                return address.slice(0, 8) + "..." + address.slice(address.length - 8);
            } else {
                return "";
            }
        }

        copyAddress(): void {
            utils.show_notification(this.$t("staking-copy").toString());
        }

        async updateLpPrice(): Promise<void> {
            try {
                const stakingService = await this.getStakingService();
                const lp = await stakingService.lp();

                const service = await this.getUniswapService();
                const balance = await service.getReserves(lp);
                const totalSupply = await service.totalSupply(lp);

                const ethSumm = new BigNumber(ConfigInstance.dynamic.usdPrices[Blockchain.Ethereum]).multipliedBy(balance[0]);
                const bcugSumm = new BigNumber(ConfigInstance.dynamic.usdPrices["BCUG"]).multipliedBy(balance[1]);
                this.lpPrice = ethSumm.plus(bcugSumm).dividedBy(new BigNumber(totalSupply));
            } catch (e) {
                VueUtils.handleError(e);
            }
        }

        async updateBalance(): Promise<void> {
            this.loadingBalance = true;
            try {
                const service = await this.getStakingService();
                const balance = await service.availableTokens(this.getUserEthereumAddress());

                this.balanceNumber = new BigNumber(balance);
                this.balanceIsZero = new BigNumber(balance).isZero();
                this.balanceBlockchain = new BigNumber(balance).toFormat(2, 1).toString();
                this.balanceFull = new BigNumber(balance).toFixed();
                if (this.tab == "BCUG") {
                    this.balanceUsd = this.toUsd(new BigNumber(balance));
                } else {
                    this.balanceUsd = await this.toUsdLp(new BigNumber(balance));
                }
            } finally {
                this.loadingBalance = false;
            }
        }

        toUsd(count: BigNumber): string {
            if (ConfigInstance.dynamic.usdPrices["BCUG"]) {
                return new BigNumber(ConfigInstance.dynamic.usdPrices["BCUG"]).multipliedBy(count).toFormat(2, 1).toString();
            } else {
                return "0";
            }
        }

        async toUsdLp(count: BigNumber): Promise<string> {
            if (this.lpPrice.isZero()) {
                await this.updateLpPrice();
                return this.lpPrice.multipliedBy(count).toFixed(2, 1).toString();
            } else {
                return this.lpPrice.multipliedBy(count).toFixed(2, 1).toString();
            }
        }

        buyLink(): string {
            let link = "https://app.uniswap.org/#/";

            if (this.tab == "BCUG") {
                link += "swap?exactField=input&exactAmount=1&use=V2&inputCurrency=ETH&outputCurrency=";
            } else {
                link += "add/v2/ETH/";
            }

            return link + ConfigInstance.ethereums.ETHEREUM.contracts.bcug;
        }

        async updateStakedBalance(): Promise<void> {
            this.loadingStaked = true;

            try {
                const service = await this.getStakingService();
                const amount = await service.staked(this.getUserEthereumAddress());

                this.staked = new BigNumber(amount).toFormat(2, 1).toString();
                this.stakedIsZero = new BigNumber(amount).isZero();
                this.stakedNumber = new BigNumber(amount);
                this.stakedFull = new BigNumber(amount).toFixed();
                if (this.tab == "BCUG") {
                    this.stakedUsd = this.toUsd(new BigNumber(amount));
                } else {
                    this.stakedUsd = await this.toUsdLp(new BigNumber(amount));
                }
            } finally {
                this.loadingStaked = false;
            }
        }

        async getAllowedStaking(): Promise<void> {
            this.loadingAllowed = true;
            try {
                const service = await this.getStakingService();
                const allowed = await service.allowance(this.getUserEthereumAddress());

                this.allowedCount = new BigNumber(allowed).toNumber();
                this.allowed = this.allowedCount > 0;
            } finally {
                this.loadingAllowed = false;
            }
        }

        isAllowedCount(): boolean {
            return Number(this.form.amount) <= this.allowedCount;
        }

        async getRewardRate(): Promise<void> {
            this.loadingApy = true;
            try {
                const service = await this.getStakingService();
                const rewardRate = await service.getRewardRate();
                this.rewardRate = new BigNumber(rewardRate);
            } finally {
                this.loadingApy = false;
            }
        }

        async approveStaking(): Promise<void> {
            this.loadingAllowBtn = true;
            const service = await this.getStakingService();
            this.allowConfirmCount = 0;
            try {
                await service.approve((confNumber: number) => {
                    this.allowConfirmCount = confNumber;
                    if (confNumber == this.confirmationCount) {
                        this.loadingAllowBtn = false;
                        utils.show_notification(this.$t("staking-approve-success").toString() + ` ${this.tab}`);
                        this.updateData();
                    }
                });
            } catch (e) {
                this.loadingAllowBtn = false;
                this.handleError(e);
            }
        }

        async stake(): Promise<void> {
            const service = await this.getStakingService();
            this.showConfirm = false;
            this.loadingStakeBtn = true;
            this.stakeCountValue = this.form.amount;
            this.form.amount = "0.1";
            this.stakeConfirmCount = 0;

            try {
                await service.stake(this.stakeCountValue.trim(), (confNumber: number) => {
                    this.stakeConfirmCount = confNumber;
                    if (confNumber == this.confirmationCount) {
                        this.loadingStakeBtn = false;
                        utils.show_notification(this.$t("staking-stake-success").toString() + ` ${this.stakeCountValue} ${this.tab}`);
                        this.updateData();
                    }
                });
            } catch (e) {
                this.loadingStakeBtn = false;
                this.handleError(e);
            }
        }

        async unstake(): Promise<void> {
            const service = await this.getStakingService();
            this.showConfirm = false;
            this.loadingUnstakeBtn = true;
            this.stakeCountValue = this.form.amount;
            this.form.amount = "0.1";
            this.unstakeConfirmCount = 0;

            try {
                await service.unstake(this.stakeCountValue.trim(), (confNumber: number) => {
                    this.unstakeConfirmCount = confNumber;
                    if (confNumber == this.confirmationCount) {
                        this.loadingUnstakeBtn = false;
                        utils.show_notification(this.$t("staking-unstake-success").toString() + ` ${this.stakeCountValue} ${this.tab}`);
                        this.updateData();
                    }
                });
            } catch (e) {
                this.loadingUnstakeBtn = false;
                this.handleError(e);
            }
        }

        async exit(): Promise<void> {
            this.loadingUnstakeBtn = true;
            this.unstakeConfirmCount = 0;
            this.showConfirmExit = false;
            const service = await this.getStakingService();
            try {
                await service.exit((confNumber: number) => {
                    this.unstakeConfirmCount = confNumber;
                    if (confNumber == this.confirmationCount) {
                        this.loadingUnstakeBtn = false;
                        utils.show_notification(
                            this.$t("staking-exit-success").toString() +
                                ` ${new BigNumber(this.rewards.replace(",", "")).plus(this.stakedNumber).toFormat(2, 1).toString()} BCUG`
                        );
                        this.updateData();
                    }
                });
            } catch (e) {
                this.loadingUnstakeBtn = false;
                this.handleError(e);
            }
        }

        async getReward(): Promise<void> {
            this.rewardLoading = true;
            this.loadingRewardBtn = true;
            const service = await this.getStakingService();
            this.rewardConfirmCount = 0;

            try {
                await service.getReward((confNumber: number) => {
                    this.rewardConfirmCount = confNumber;
                    if (confNumber == this.confirmationCount) {
                        this.loadingRewardBtn = false;
                        utils.show_notification(this.$t("staking-reward-success").toString() + ` ${this.rewards} BCUG`);
                        this.updateData();
                    }
                });
            } catch (e) {
                this.loadingRewardBtn = false;
                this.handleError(e);
            }
        }

        handleError(e: { message?: string }): void {
            console.error(e);
            const msg = e.message ? e.message : "";
            if (msg) utils.show_notification(msg);
        }

        async updateRewards(): Promise<void> {
            this.loadingReward = true;
            try {
                const service = await this.getStakingService();
                const amount = await service.earned(this.getUserEthereumAddress());

                this.rewardIsZero = new BigNumber(amount).isZero();
                this.rewards = new BigNumber(amount).toFormat(2, 1).toString();
                this.rewardsUsd = this.toUsd(new BigNumber(amount));
                this.rewardsFull = new BigNumber(amount).toFixed();
            } finally {
                this.loadingReward = false;
            }
        }

        async updateTotalSupply(): Promise<void> {
            this.loadingSupply = true;

            try {
                const service = await this.getStakingService();
                const amount = await service.totalStake();

                this.totalSupply = new BigNumber(amount);
                this.supply = this.toUsd(new BigNumber(amount));
            } finally {
                this.loadingSupply = false;
            }
        }

        /**
         * In case when user not authorized check is user browser has active metamask
         * It will allow user to participate staking without game login
         */
        async checkUnauthorizedMetamask(): Promise<void> {
            const currentProvider = (await CutiesApiFaucet.getApi(Blockchain.Ethereum)).get_provider();

            // when no active game provider
            if (!currentProvider) {
                await this.initProvider();
            }
        }

        async initProvider(services = true): Promise<void> {
            const ethereumProvider = (await detectEthereumProvider({ mustBeMetaMask: true })) as EthereumProvider;
            if (!ethereumProvider) {
                // metamask not detected
                throw new Error("No metamask");
            }

            const config = ConfigInstance.ethereums.ETHEREUM;
            const web3MetamaskProvider = await Web3MetamaskProvider.create(Blockchain.Ethereum, {
                kind: "metamask",
                ethereumProvider,
                config,
            });

            this.activeMetamaskAccount = await web3MetamaskProvider.getWalletAddress();
            if (services) {
                this.stakingService = new EthereumStakingService(web3MetamaskProvider, this.contractName, config);
                this.uniswapService = new EthereumUniswapService(web3MetamaskProvider, config);
            }

            this.anonymous = true;

            ethereumProvider.on("accountsChanged", async () => {
                this.activeMetamaskAccount = await web3MetamaskProvider.getWalletAddress();
                this.updateData();
            });
        }

        // dirty workaround for user session check, we must ensure that user has active provider
        async getStakingService(): Promise<EthereumStakingService> {
            return new Promise<EthereumStakingService>((resolve, reject) => {
                if (!this.stakingService) {
                    LoginManager.run_unlock(Blockchain.Ethereum, (wallet) => {
                        const provider = wallet.get_provider();

                        if ("confirmGasPrice" in provider) {
                            this.stakingService = new EthereumStakingService(provider, this.contractName, ConfigInstance.ethereums.ETHEREUM);
                            resolve(this.stakingService);
                        } else {
                            reject(new Error("Unsupported provider is chosen"));
                        }
                    });
                } else {
                    resolve(this.stakingService);
                }
            });
        }

        async getUniswapService(): Promise<EthereumUniswapService> {
            return new Promise<EthereumUniswapService>((resolve, reject) => {
                if (!this.uniswapService) {
                    LoginManager.run_unlock(Blockchain.Ethereum, (wallet) => {
                        const provider = wallet.get_provider();

                        if ("confirmGasPrice" in provider) {
                            this.uniswapService = new EthereumUniswapService(provider, ConfigInstance.ethereums.ETHEREUM);
                            resolve(this.uniswapService);
                        } else {
                            reject(new Error("Unsupported provider is chosen"));
                        }
                    });
                } else {
                    resolve(this.uniswapService);
                }
            });
        }

        coinName(tab: string): string {
            return this.$t("token-name-" + tab).toString();
        }

        submitForm(): void {
            this.$refs.sendForm.validate((valid) => {
                this.validNumber = valid ? true : false;
            });
        }
    }
